import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api, CATEGORY_COLORS } from "@/lib/api";
import { useCategories } from "@/context/CategoriesContext";
import { ArrowLeft, ArrowRight, Calendar, MapPin, DollarSign, Clock, ExternalLink, Loader2, Trophy } from "lucide-react";

function fmtDateLong(s) {
  if (!s) return "";
  try { return new Date(s + "T00:00:00").toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric", year: "numeric" }); } catch { return s; }
}

function DateBadge({ dateStr }) {
  if (!dateStr) return null;
  let d;
  try { d = new Date(dateStr + "T00:00:00"); } catch { return null; }
  const month = d.toLocaleDateString(undefined, { month: "short" }).toUpperCase();
  const day = d.getDate();
  const year = d.getFullYear();
  return (
    <div className="shrink-0 w-24 rounded-2xl bg-white border-2 border-[#FF9F1C] shadow-[0_8px_20px_rgb(255,159,28,0.15)] overflow-hidden text-center" data-testid="comp-date-badge">
      <div className="bg-[#FF9F1C] text-white text-[11px] font-black uppercase tracking-widest py-1.5">{month}</div>
      <div className="font-display font-black text-4xl leading-none pt-2 text-[#1E1E24]">{day}</div>
      <div className="text-[10px] text-[#7A7D8C] pb-2 pt-1 font-semibold">{year}</div>
    </div>
  );
}

export default function CompetitionDetail() {
  const { categories } = useCategories();
  const { id } = useParams();
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    setLoading(true);
    api.get(`/events/public/${id}`)
      .then((r) => setEvent(r.data))
      .catch((e) => setError(e?.response?.status === 404 ? "This competition isn't currently available." : "Could not load competition."))
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) {
    return (
      <div className="min-h-[60vh] grid place-items-center">
        <div className="text-[#7A7D8C] inline-flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading competition…</div>
      </div>
    );
  }
  if (error || !event) {
    return (
      <div className="max-w-3xl mx-auto px-5 md:px-10 py-24 text-center" data-testid="comp-detail-error">
        <h1 className="font-display font-black text-3xl">{error || "Not found"}</h1>
        <Link to="/competitions" className="mt-6 inline-flex items-center gap-2 px-5 py-2 rounded-full bg-[#FF9F1C] text-white font-bold text-sm shadow-[0_8px_20px_rgb(255,159,28,0.3)]"><ArrowLeft className="w-4 h-4" /> Back to competitions</Link>
      </div>
    );
  }

  return (
    <div>
      <section className="max-w-5xl mx-auto px-5 md:px-10 pt-12 md:pt-16 pb-8">
        <Link to="/competitions" data-testid="comp-detail-back" className="inline-flex items-center gap-1 text-sm font-semibold text-[#FF9F1C] hover:gap-2 transition-all"><ArrowLeft className="w-4 h-4" /> All competitions</Link>

        <div className="mt-6 flex flex-wrap gap-6 items-start">
          <DateBadge dateStr={event.start_date} />
          <div className="flex-1 min-w-0">
            <span className="text-xs uppercase tracking-widest font-black text-[#FF9F1C]">Competition</span>
            <h1 className="mt-2 font-display font-black text-4xl sm:text-5xl tracking-tight leading-none" data-testid="comp-detail-title">{event.title}</h1>
            <div className="mt-4 flex flex-wrap gap-x-6 gap-y-2 text-sm text-[#1E1E24]">
              <span className="inline-flex items-center gap-1.5"><Calendar className="w-4 h-4 text-[#FF9F1C]" /> {fmtDateLong(event.start_date)}{event.end_date && event.end_date !== event.start_date ? ` – ${fmtDateLong(event.end_date)}` : ""}</span>
              {(event.start_time || event.end_time) && <span className="inline-flex items-center gap-1.5"><Clock className="w-4 h-4 text-[#FF9F1C]" /> {event.start_time}{event.end_time ? ` – ${event.end_time}` : ""}</span>}
              {event.location && <span className="inline-flex items-center gap-1.5"><MapPin className="w-4 h-4 text-[#FF9F1C]" /> {event.location}</span>}
              {event.registration_fee != null && <span className="inline-flex items-center gap-1.5"><DollarSign className="w-4 h-4 text-[#FF9F1C]" /> {event.registration_fee}</span>}
            </div>
          </div>
        </div>
      </section>

      <section className="max-w-5xl mx-auto px-5 md:px-10 py-6 grid lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 space-y-8">
          {event.description && (
            <div className="rounded-3xl bg-white border border-slate-100 p-6 md:p-8 shadow-[0_4px_20px_rgb(0,0,0,0.03)]">
              <h2 className="font-display font-black text-2xl">About this competition</h2>
              <p className="mt-4 whitespace-pre-wrap text-[#1E1E24]/90 leading-relaxed" data-testid="comp-detail-description">{event.description}</p>
            </div>
          )}

          <div className="rounded-3xl bg-white border border-slate-100 p-6 md:p-8 shadow-[0_4px_20px_rgb(0,0,0,0.03)]">
            <h2 className="font-display font-black text-lg">Competition format</h2>
            <div className="mt-6 grid gap-6 sm:grid-cols-2">
              <RoundCard step="Round 1" title="Timing Round" body="Each participant completes one Timing Solve. The recorded time helps organizers confirm the appropriate speed category." />
              <RoundCard step="Round 2" title="Competition" body="Each participant completes one timed competition solve. This time determines the final standings within their category." />
            </div>
            <div className="mt-8">
              <h3 className="font-display font-bold text-lg">Speed categories</h3>
              <ul className="mt-3 grid sm:grid-cols-2 gap-2">
                {categories.map((row) => (
                  <li key={row.value} className="flex items-center gap-3 text-sm font-semibold" data-testid={`competition-category-${row.value}`}>
                    <span className={`w-3 h-3 rounded-sm border ${CATEGORY_COLORS[row.value] || ""}`} />
                    {row.label}
                  </li>
                ))}
              </ul>
              <p className="mt-4 text-sm text-[#7A7D8C]">Minimum <b>2 competitors</b> per category. Timing Solves above 120 seconds are recorded and reviewed by organizers for manual category placement.</p>
            </div>
          </div>

          <div className="rounded-3xl bg-[#FFBF69]/15 border border-[#FFBF69]/40 p-6 md:p-8">
            <h3 className="font-display font-bold text-lg">Gear &amp; ground rules</h3>
            <ul className="mt-4 space-y-2 text-sm">
              <li>All cubers use official <b>GAN i Carry Bluetooth cubes</b> provided by Naptown Cubers.</li>
              <li>Personal cubes are welcome for practice, but not for competition solves.</li>
              <li>A parent / legal guardian must complete registration for every competitor.</li>
              <li>Judges are patient and encouraging — this is a personal-best-first environment.</li>
            </ul>
          </div>
        </div>

        <aside className="space-y-6">
          <div className="rounded-3xl bg-white border-2 border-[#FF9F1C] p-6 shadow-[0_12px_40px_rgb(255,159,28,0.15)] sticky top-6" data-testid="comp-detail-register-card">
            <div className="text-[11px] uppercase tracking-widest font-black text-[#FF9F1C]">Register</div>
            {event.registration_fee != null && <div className="mt-1 font-display font-black text-4xl">${Number(event.registration_fee).toFixed(0)}</div>}
            <p className="mt-2 text-sm text-[#7A7D8C]">Registration is open to students in grades 3–12 and reserves a place in this competition. Paid registrations are refundable until event day.</p>
            {event.registration_url ? (
              <a href={event.registration_url} target="_blank" rel="noreferrer" data-testid="comp-detail-register-btn" className="mt-5 w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full bg-[#FF9F1C] text-white font-bold shadow-[0_10px_24px_rgb(255,159,28,0.35)] hover:-translate-y-0.5 transition-transform">
                Register <ExternalLink className="w-4 h-4" />
              </a>
            ) : (
              <Link to={`/register?event=${event.id}`} data-testid="comp-detail-register-btn" className="mt-5 w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full bg-[#FF9F1C] text-white font-bold shadow-[0_10px_24px_rgb(255,159,28,0.35)] hover:-translate-y-0.5 transition-transform">
                {event.is_free || event.registration_fee === 0 ? "Register free" : "Register & pay"} <ArrowRight className="w-4 h-4" />
              </Link>
            )}
            <Link to="/contact" className="mt-3 w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full bg-white border border-slate-200 font-bold text-sm hover:border-[#FF9F1C] hover:text-[#FF9F1C]">
              Ask a question
            </Link>
          </div>

          <div className="rounded-3xl bg-white border border-slate-100 p-6">
            <div className="flex items-center gap-2 text-[11px] uppercase tracking-widest font-black text-[#7A7D8C]"><Trophy className="w-3.5 h-3.5" /> Two rounds</div>
            <p className="mt-3 text-sm text-[#1E1E24]/90" data-testid="competition-two-rounds-summary">The event consists of one Timing Solve for category placement and one competition solve for the final standings.</p>
          </div>
        </aside>
      </section>
    </div>
  );
}

function RoundCard({ step, title, body }) {
  return (
    <div className="rounded-2xl border border-slate-100 bg-[#FDFFFC] p-5">
      <div className="text-[11px] uppercase tracking-widest font-black text-[#FF9F1C]">{step}</div>
      <div className="mt-1 font-display font-black text-xl">{title}</div>
      <p className="mt-2 text-sm text-[#1E1E24]/90 leading-relaxed">{body}</p>
    </div>
  );
}
